import { api } from "../../api/apiSlice";

// Petty Cash (Fund/Category/Ledger/Voucher/Refill/Reconciliation/Adjustment)
// - full React port of the Flutter-only module (2026-08-29), against
// TexasAlarms.Api's already-complete src/PettyCash/PettyCashController.cs.
// Started life here as 3 read-only GETs for the Dashboard; now the full
// surface, following the exact query/mutation/tag convention used by
// features/stock/stockApi.js. Every list endpoint returns the shared
// PagedResult<T> shape ({items, totalCount, page, pageSize}) except
// getPettyCashFunds/getPettyCashCategories, which return a plain array.
export const pettyCashApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // ---- Funds ----
    getPettyCashFunds: builder.query({
      query: (params) => ({ url: "/pettycash/funds", params }),
      providesTags: (result) =>
        result
          ? [
              ...result.map((f) => ({ type: "PettyCashFund", id: f.fundId })),
              { type: "PettyCashFund", id: "LIST" },
            ]
          : [{ type: "PettyCashFund", id: "LIST" }],
    }),
    getPettyCashFund: builder.query({
      query: (fundId) => ({ url: `/pettycash/funds/${fundId}` }),
      providesTags: (result, error, fundId) => [{ type: "PettyCashFund", id: fundId }],
    }),
    createPettyCashFund: builder.mutation({
      query: (body) => ({ url: "/pettycash/funds", method: "POST", data: body }),
      invalidatesTags: [{ type: "PettyCashFund", id: "LIST" }],
    }),
    updatePettyCashFund: builder.mutation({
      query: ({ fundId, ...body }) => ({ url: `/pettycash/funds/${fundId}`, method: "PUT", data: body }),
      invalidatesTags: (result, error, { fundId }) => [
        { type: "PettyCashFund", id: fundId },
        { type: "PettyCashFund", id: "LIST" },
      ],
    }),

    // ---- Categories ----
    // Own endpoint rather than reading /settings, which is admin-only: an
    // ordinary requester could not see their own self-pay ceiling, so the
    // Pay button never appeared on their under-ceiling voucher.
    getSelfPayCeiling: builder.query({
      query: () => ({ url: "/pettycash/self-pay-ceiling" }),
    }),
    getPettyCashCategories: builder.query({
      query: () => ({ url: "/pettycash/categories" }),
      providesTags: [{ type: "PettyCashCategory", id: "LIST" }],
    }),
    createPettyCashCategory: builder.mutation({
      query: (body) => ({ url: "/pettycash/categories", method: "POST", data: body }),
      invalidatesTags: [{ type: "PettyCashCategory", id: "LIST" }],
    }),
    updatePettyCashCategory: builder.mutation({
      query: ({ categoryId, ...body }) => ({ url: `/pettycash/categories/${categoryId}`, method: "PUT", data: body }),
      invalidatesTags: [{ type: "PettyCashCategory", id: "LIST" }],
    }),

    // ---- Vouchers ----
    listPettyCashVouchers: builder.query({
      query: (params) => ({ url: "/pettycash/vouchers", params }),
      providesTags: (result) =>
        result?.items
          ? [
              ...result.items.map((v) => ({ type: "PettyCashVoucher", id: v.recordGuid })),
              { type: "PettyCashVoucher", id: "LIST" },
            ]
          : [{ type: "PettyCashVoucher", id: "LIST" }],
    }),
    getPettyCashVoucher: builder.query({
      query: (recordGuid) => ({ url: `/pettycash/vouchers/${recordGuid}` }),
      providesTags: (result, error, recordGuid) => [{ type: "PettyCashVoucher", id: recordGuid }],
    }),
    createPettyCashVoucher: builder.mutation({
      query: (body) => ({ url: "/pettycash/vouchers", method: "POST", data: body }),
      invalidatesTags: [{ type: "PettyCashVoucher", id: "LIST" }],
    }),
    approvePettyCashVoucher: builder.mutation({
      query: ({ recordGuid, ...body }) => ({ url: `/pettycash/vouchers/${recordGuid}/approve`, method: "POST", data: body }),
      invalidatesTags: (result, error, { recordGuid }) => [
        { type: "PettyCashVoucher", id: recordGuid },
        { type: "PettyCashVoucher", id: "LIST" },
      ],
    }),
    rejectPettyCashVoucher: builder.mutation({
      query: ({ recordGuid, rejectionReason }) => ({
        url: `/pettycash/vouchers/${recordGuid}/reject`,
        method: "POST",
        data: { rejectionReason },
      }),
      invalidatesTags: (result, error, { recordGuid }) => [
        { type: "PettyCashVoucher", id: recordGuid },
        { type: "PettyCashVoucher", id: "LIST" },
      ],
    }),
    cancelPettyCashVoucher: builder.mutation({
      query: (recordGuid) => ({ url: `/pettycash/vouchers/${recordGuid}/cancel`, method: "POST" }),
      invalidatesTags: (result, error, recordGuid) => [
        { type: "PettyCashVoucher", id: recordGuid },
        { type: "PettyCashVoucher", id: "LIST" },
      ],
    }),
    payPettyCashVoucher: builder.mutation({
      query: ({ recordGuid, ...body }) => ({ url: `/pettycash/vouchers/${recordGuid}/pay`, method: "POST", data: body }),
      invalidatesTags: (result, error, { recordGuid }) => [
        { type: "PettyCashVoucher", id: recordGuid },
        { type: "PettyCashVoucher", id: "LIST" },
        { type: "PettyCashLedger", id: "LIST" },
        { type: "PettyCashFund", id: "LIST" },
      ],
    }),
    uploadVoucherReceipt: builder.mutation({
      query: ({ recordGuid, file }) => {
        const formData = new FormData();
        formData.append("file", file);
        return { url: `/pettycash/vouchers/${recordGuid}/receipt`, method: "POST", data: formData };
      },
      invalidatesTags: (result, error, { recordGuid }) => [{ type: "PettyCashVoucher", id: recordGuid }],
    }),
    // Authenticated file - fetched as a Blob (axiosBaseQuery's responseType
    // passthrough) then handed to the browser as a temporary object URL,
    // same discipline as EmployeeDocuments' download/view.
    getVoucherReceiptContent: builder.query({
      query: (recordGuid) => ({ url: `/pettycash/vouchers/${recordGuid}/receipt`, responseType: "blob" }),
      // Shown inline on the voucher since 2026-09-30, so a replaced receipt must refetch:
      // upload/delete already invalidate this voucher's tag.
      providesTags: (result, error, recordGuid) => [{ type: "PettyCashVoucher", id: recordGuid }],
    }),
    deleteVoucherReceipt: builder.mutation({
      query: (recordGuid) => ({ url: `/pettycash/vouchers/${recordGuid}/receipt`, method: "DELETE" }),
      invalidatesTags: (result, error, recordGuid) => [{ type: "PettyCashVoucher", id: recordGuid }],
    }),
    // Voucher supporting documents (Batch198, 2026-09-30): cheque image, bank slip,
    // invoice, other - many per voucher, at any status. Separate from the receipt.
    // Tagged on the existing PettyCashVoucher type (a new tag type would be a silent
    // no-op unless registered on the base slice) under an "<guid>:attachments" id.
    listVoucherAttachments: builder.query({
      query: (recordGuid) => ({ url: `/pettycash/vouchers/${recordGuid}/attachments` }),
      providesTags: (result, error, recordGuid) => [{ type: "PettyCashVoucher", id: `${recordGuid}:attachments` }],
    }),
    uploadVoucherAttachment: builder.mutation({
      query: ({ recordGuid, file, kind }) => {
        const formData = new FormData();
        formData.append("file", file);
        if (kind) formData.append("kind", kind);
        return { url: `/pettycash/vouchers/${recordGuid}/attachments`, method: "POST", data: formData };
      },
      invalidatesTags: (result, error, { recordGuid }) => [{ type: "PettyCashVoucher", id: `${recordGuid}:attachments` }],
    }),
    // Bytes on demand only; never changes once saved, so it is kept for a while.
    getVoucherAttachmentContent: builder.query({
      query: ({ recordGuid, attachmentRecordGuid }) => ({
        url: `/pettycash/vouchers/${recordGuid}/attachments/${attachmentRecordGuid}/content`,
        responseType: "blob",
      }),
      keepUnusedDataFor: 600,
    }),
    deleteVoucherAttachment: builder.mutation({
      query: ({ recordGuid, attachmentRecordGuid }) => ({
        url: `/pettycash/vouchers/${recordGuid}/attachments/${attachmentRecordGuid}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { recordGuid }) => [{ type: "PettyCashVoucher", id: `${recordGuid}:attachments` }],
    }),
    // Refill supporting document (Batch195, 2026-09-22) - same contract as the voucher receipt.
    uploadPettyCashRefillAttachment: builder.mutation({
      query: ({ recordGuid, file }) => {
        const formData = new FormData();
        formData.append("file", file);
        return { url: `/pettycash/refills/${recordGuid}/attachment`, method: "POST", data: formData };
      },
      invalidatesTags: (result, error, { recordGuid }) => [
        { type: "PettyCashRefill", id: recordGuid },
        { type: "PettyCashRefill", id: "LIST" },
      ],
    }),
    getPettyCashRefillAttachmentContent: builder.query({
      query: (recordGuid) => ({ url: `/pettycash/refills/${recordGuid}/attachment`, responseType: "blob" }),
      // Shown inline on the refill since 2026-10-13 (like the voucher receipt), so a
      // replaced document must refetch: upload/delete already invalidate this refill's tag.
      providesTags: (result, error, recordGuid) => [{ type: "PettyCashRefill", id: recordGuid }],
    }),
    deletePettyCashRefillAttachment: builder.mutation({
      query: (recordGuid) => ({ url: `/pettycash/refills/${recordGuid}/attachment`, method: "DELETE" }),
      invalidatesTags: (result, error, recordGuid) => [
        { type: "PettyCashRefill", id: recordGuid },
        { type: "PettyCashRefill", id: "LIST" },
      ],
    }),

    // ---- Voucher returns (Batch168) ----
    // Unspent balance handed back against a PAID voucher. Credits the fund the
    // voucher was paid from straight away, so the voucher (its returnedAmount),
    // the fund balances and the ledger all refresh.
    listPettyCashVoucherReturns: builder.query({
      query: (recordGuid) => ({ url: `/pettycash/vouchers/${recordGuid}/returns` }),
      providesTags: (result, error, recordGuid) => [{ type: "PettyCashVoucherReturn", id: recordGuid }],
    }),
    // The Returns page's register - every return recorded, newest first (paged).
    listPettyCashReturns: builder.query({
      query: (params) => ({ url: "/pettycash/returns", params }),
      providesTags: [{ type: "PettyCashVoucherReturn", id: "LIST" }],
    }),
    recordPettyCashVoucherReturn: builder.mutation({
      query: ({ recordGuid, ...body }) => ({ url: `/pettycash/vouchers/${recordGuid}/returns`, method: "POST", data: body }),
      invalidatesTags: (result, error, { recordGuid }) => [
        { type: "PettyCashVoucherReturn", id: recordGuid },
        { type: "PettyCashVoucherReturn", id: "LIST" },
        { type: "PettyCashVoucher", id: recordGuid },
        { type: "PettyCashVoucher", id: "LIST" },
        { type: "PettyCashLedger", id: "LIST" },
        { type: "PettyCashFund", id: "LIST" },
      ],
    }),

    // ---- Refills ----
    listPettyCashRefills: builder.query({
      query: (params) => ({ url: "/pettycash/refills", params }),
      providesTags: (result) =>
        result?.items
          ? [
              ...result.items.map((r) => ({ type: "PettyCashRefill", id: r.recordGuid })),
              { type: "PettyCashRefill", id: "LIST" },
            ]
          : [{ type: "PettyCashRefill", id: "LIST" }],
    }),
    getPettyCashRefill: builder.query({
      query: (recordGuid) => ({ url: `/pettycash/refills/${recordGuid}` }),
      providesTags: (result, error, recordGuid) => [{ type: "PettyCashRefill", id: recordGuid }],
    }),
    createPettyCashRefill: builder.mutation({
      query: (body) => ({ url: "/pettycash/refills", method: "POST", data: body }),
      invalidatesTags: [{ type: "PettyCashRefill", id: "LIST" }],
    }),
    approvePettyCashRefill: builder.mutation({
      query: ({ recordGuid, ...body }) => ({ url: `/pettycash/refills/${recordGuid}/approve`, method: "POST", data: body }),
      invalidatesTags: (result, error, { recordGuid }) => [
        { type: "PettyCashRefill", id: recordGuid },
        { type: "PettyCashRefill", id: "LIST" },
      ],
    }),
    rejectPettyCashRefill: builder.mutation({
      query: ({ recordGuid, rejectionReason }) => ({
        url: `/pettycash/refills/${recordGuid}/reject`,
        method: "POST",
        data: { rejectionReason },
      }),
      invalidatesTags: (result, error, { recordGuid }) => [
        { type: "PettyCashRefill", id: recordGuid },
        { type: "PettyCashRefill", id: "LIST" },
      ],
    }),
    cancelPettyCashRefill: builder.mutation({
      query: (recordGuid) => ({ url: `/pettycash/refills/${recordGuid}/cancel`, method: "POST" }),
      invalidatesTags: (result, error, recordGuid) => [
        { type: "PettyCashRefill", id: recordGuid },
        { type: "PettyCashRefill", id: "LIST" },
      ],
    }),
    // Batch215 (2026-10-22): fund refill raised from a voucher (cheque no. + cheque photo).
    // The list also provides each refill's own tag, so approve/reject/cancel on the refill
    // page (which invalidate PettyCashRefill <guid>) refresh the voucher's card too.
    listVoucherRefills: builder.query({
      query: (recordGuid) => ({ url: `/pettycash/vouchers/${recordGuid}/refills` }),
      providesTags: (result, error, recordGuid) => [
        { type: "PettyCashVoucher", id: `${recordGuid}:refills` },
        ...(Array.isArray(result) ? result.map((r) => ({ type: "PettyCashRefill", id: r.recordGuid })) : []),
      ],
    }),
    createVoucherRefill: builder.mutation({
      query: ({ recordGuid, ...body }) => ({ url: `/pettycash/vouchers/${recordGuid}/refills`, method: "POST", data: body }),
      invalidatesTags: (result, error, { recordGuid }) => [
        { type: "PettyCashVoucher", id: `${recordGuid}:refills` },
        { type: "PettyCashVoucher", id: recordGuid },
        { type: "PettyCashRefill", id: "LIST" },
      ],
    }),
    issuePettyCashRefill: builder.mutation({
      query: ({ recordGuid, ...body }) => ({ url: `/pettycash/refills/${recordGuid}/issue`, method: "POST", data: body }),
      invalidatesTags: (result, error, { recordGuid }) => [
        { type: "PettyCashRefill", id: recordGuid },
        { type: "PettyCashRefill", id: "LIST" },
        { type: "PettyCashLedger", id: "LIST" },
        { type: "PettyCashFund", id: "LIST" },
      ],
    }),

    // ---- Reconciliations ----
    listPettyCashReconciliations: builder.query({
      query: (params) => ({ url: "/pettycash/reconciliations", params }),
      providesTags: (result) =>
        result?.items
          ? [
              ...result.items.map((r) => ({ type: "PettyCashReconciliation", id: r.recordGuid })),
              { type: "PettyCashReconciliation", id: "LIST" },
            ]
          : [{ type: "PettyCashReconciliation", id: "LIST" }],
    }),
    getPettyCashReconciliation: builder.query({
      query: (recordGuid) => ({ url: `/pettycash/reconciliations/${recordGuid}` }),
      providesTags: (result, error, recordGuid) => [{ type: "PettyCashReconciliation", id: recordGuid }],
    }),
    createPettyCashReconciliation: builder.mutation({
      query: (body) => ({ url: "/pettycash/reconciliations", method: "POST", data: body }),
      invalidatesTags: [{ type: "PettyCashReconciliation", id: "LIST" }],
    }),
    approvePettyCashReconciliation: builder.mutation({
      query: ({ recordGuid, ...body }) => ({ url: `/pettycash/reconciliations/${recordGuid}/approve`, method: "POST", data: body }),
      invalidatesTags: (result, error, { recordGuid }) => [
        { type: "PettyCashReconciliation", id: recordGuid },
        { type: "PettyCashReconciliation", id: "LIST" },
      ],
    }),
    rejectPettyCashReconciliation: builder.mutation({
      query: ({ recordGuid, rejectionReason }) => ({
        url: `/pettycash/reconciliations/${recordGuid}/reject`,
        method: "POST",
        data: { rejectionReason },
      }),
      invalidatesTags: (result, error, { recordGuid }) => [
        { type: "PettyCashReconciliation", id: recordGuid },
        { type: "PettyCashReconciliation", id: "LIST" },
      ],
    }),

    // ---- Ledger & Adjustment ----
    getPettyCashLedger: builder.query({
      query: (params) => ({ url: "/pettycash/ledger", params }),
      providesTags: [{ type: "PettyCashLedger", id: "LIST" }],
    }),
    postPettyCashAdjustment: builder.mutation({
      query: (body) => ({ url: "/pettycash/ledger/adjustment", method: "POST", data: body }),
      invalidatesTags: (result, error, { fundId }) => [
        { type: "PettyCashFund", id: fundId },
        { type: "PettyCashFund", id: "LIST" },
        { type: "PettyCashLedger", id: "LIST" },
      ],
    }),

    // ---- Summary ----
    getPettyCashSummary: builder.query({
      query: (params) => ({ url: "/pettycash/summary", params }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetSelfPayCeilingQuery,
  useGetPettyCashFundsQuery,
  useGetPettyCashFundQuery,
  useCreatePettyCashFundMutation,
  useUpdatePettyCashFundMutation,
  useGetPettyCashCategoriesQuery,
  useCreatePettyCashCategoryMutation,
  useUpdatePettyCashCategoryMutation,
  useListPettyCashVouchersQuery,
  useGetPettyCashVoucherQuery,
  useCreatePettyCashVoucherMutation,
  useApprovePettyCashVoucherMutation,
  useRejectPettyCashVoucherMutation,
  useCancelPettyCashVoucherMutation,
  usePayPettyCashVoucherMutation,
  useUploadVoucherReceiptMutation,
  useGetVoucherReceiptContentQuery,
  useLazyGetVoucherReceiptContentQuery,
  useDeleteVoucherReceiptMutation,
  useListVoucherAttachmentsQuery,
  useUploadVoucherAttachmentMutation,
  useGetVoucherAttachmentContentQuery,
  useLazyGetVoucherAttachmentContentQuery,
  useDeleteVoucherAttachmentMutation,
  useUploadPettyCashRefillAttachmentMutation,
  useGetPettyCashRefillAttachmentContentQuery,
  useLazyGetPettyCashRefillAttachmentContentQuery,
  useDeletePettyCashRefillAttachmentMutation,
  useListPettyCashVoucherReturnsQuery,
  useListPettyCashReturnsQuery,
  useRecordPettyCashVoucherReturnMutation,
  useListPettyCashRefillsQuery,
  useGetPettyCashRefillQuery,
  useCreatePettyCashRefillMutation,
  useApprovePettyCashRefillMutation,
  useRejectPettyCashRefillMutation,
  useCancelPettyCashRefillMutation,
  useIssuePettyCashRefillMutation,
  useListVoucherRefillsQuery,
  useCreateVoucherRefillMutation,
  useListPettyCashReconciliationsQuery,
  useGetPettyCashReconciliationQuery,
  useCreatePettyCashReconciliationMutation,
  useApprovePettyCashReconciliationMutation,
  useRejectPettyCashReconciliationMutation,
  useGetPettyCashLedgerQuery,
  usePostPettyCashAdjustmentMutation,
  useGetPettyCashSummaryQuery,
} = pettyCashApi;
